import React, { useCallback, useMemo } from 'react';
import { Box, Skeleton, TableContainer, Typography } from '@mui/material';
import type { BaseEntity } from 'c-sdk';
import type { EntityListColumn, PaginatedTableProps } from 'c-pagination';
import { Table, TableBody, TableCell, TableHead, TableRow } from 'c-components';
import { NetworkRequestState, paginatedDataToSearchPayload } from 'c-data-layer';
import { useCommonTranslation } from 'c-translation';
import { useUserPermissions } from 'c-auth-module/Hooks';
import { IfHasAllPermissions, IfUserIsOneOfTypes } from 'c-auth-module/Components';
import { useAuthenticatedUser } from 'c-hooks';
import PaginatedEntityRow from '../PaginatedEntityRow/PaginatedEntityRow';
import EntityDataColumn from '../EntityDataColumn/EntityDataColumn';
import EntityColumnHeader from '../EntityColumnHeader/EntityColumnHeader';

const SKELETON_ROW_COUNT = 5;

function PaginatedEntityList<Entity extends BaseEntity>({
    paginationData,
    columns,
    rows,
    RenderColumnData = EntityDataColumn,
    onSearch,
    tag,
    tableContainerProps,
    tableProps,
    baseEntityName,
    generateRowSx,
    disabledRowDividers,
    dense,
    emptyMessage = 'Pagination.emptyResults',
}: PaginatedTableProps<Entity>) {
    const onOrderByChange = useCallback(
        (column: EntityListColumn<Entity>, orderByKey: string) => {
            const currentDirection = paginationData.direction;
            const currentOrderBy = paginationData.orderBy;
            let newDirection = currentDirection;

            let flip = false;
            if (currentOrderBy === orderByKey) {
                flip = true;
            }

            if (flip) {
                newDirection = currentDirection === 'asc' ? 'desc' : 'asc';
            } else {
                newDirection = 'asc';
            }

            onSearch(
                paginationData.meta.pagination.current_page,
                paginatedDataToSearchPayload(paginationData, {
                    orderBy: orderByKey,
                    direction: newDirection,
                }),
            );
        },
        [onSearch, paginationData],
    );

    const { hasAll } = useUserPermissions();
    const { chosenUserType, isAdmin } = useAuthenticatedUser();
    const actualColumns = useMemo(
        () =>
            columns.filter(col => {
                const typeCheck =
                    isAdmin ||
                    (col.userTypes ?? []).length === 0 ||
                    (col.userTypes ?? []).indexOf(chosenUserType) !== -1;
                const permCheck = hasAll(col.permissions ?? []);

                return typeCheck && permCheck;
            }),
        [columns, hasAll, chosenUserType, isAdmin],
    );

    // Only once a search has actually answered with nothing: not before the first response, not
    // while a (re)search is in flight, and not after an error (the renderer shows that instead).
    const loadingState = paginationData?.loadingState?.state;
    const showEmptyRow =
        rows.length === 0 &&
        paginationData?.meta?.pagination != null &&
        loadingState !== NetworkRequestState.InProgress &&
        loadingState !== NetworkRequestState.Error;
    // Nothing on screen yet to dim (the first load, or a search after an empty result): show the
    // table's shape instead. The renderer's progress bar announces the wait.
    const showSkeletonRows = rows.length === 0 && loadingState === NetworkRequestState.InProgress;
    const t = useCommonTranslation();

    const table = (
        <Table {...tableProps} sx={{ borderCollapse: 'separate' }}>
            <TableHead>
                <TableRow isHeader dense={dense}>
                    {actualColumns.map((col, colIdx) => (
                        <TableCell
                            key={`${col.entityName}-${col.field}-${col.headerTitle}`}
                            isHeader
                            dense={dense}
                        >
                            <IfUserIsOneOfTypes roles={col.userTypes}>
                                <IfHasAllPermissions permissions={col.permissions}>
                                    <EntityColumnHeader
                                        column={col}
                                        paginationData={paginationData}
                                        onOrderByClicked={onOrderByChange}
                                    />
                                </IfHasAllPermissions>
                            </IfUserIsOneOfTypes>
                        </TableCell>
                    ))}
                </TableRow>
            </TableHead>
            <TableBody>
                {rows.map(row => (
                    <PaginatedEntityRow
                        key={row.id}
                        row={row}
                        columns={actualColumns}
                        RenderColumnData={RenderColumnData}
                        tag={tag}
                        onClick={row.onClick}
                        paginationData={paginationData}
                        baseEntityName={baseEntityName}
                        generateSx={generateRowSx}
                        disabledRowDividers={disabledRowDividers}
                        dense={dense}
                    />
                ))}
                {showSkeletonRows &&
                    Array.from({ length: SKELETON_ROW_COUNT }, (_, rowIdx) => (
                        <TableRow key={`skeleton-${rowIdx}`} dense={dense} aria-hidden>
                            {actualColumns.map(col => (
                                <TableCell
                                    key={`${col.entityName}-${col.field}-${col.headerTitle}`}
                                    dense={dense}
                                >
                                    <Skeleton variant="text" />
                                </TableCell>
                            ))}
                        </TableRow>
                    ))}
                {showEmptyRow && (
                    <TableRow dense={dense}>
                        <TableCell colSpan={Math.max(actualColumns.length, 1)} align="center">
                            <Typography variant="body2" color="text.secondary" py={2}>
                                {t(emptyMessage)}
                            </Typography>
                        </TableCell>
                    </TableRow>
                )}
            </TableBody>
        </Table>
    );
    return (
        <TableContainer component={Box} {...tableContainerProps}>
            {table}
        </TableContainer>
    );
}

export default PaginatedEntityList;
